import { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { api } from '../lib/api';
import { Button } from '@/components/ui/Button';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '@/components/ui/Card';
import { ProgressBar } from '@/components/ui/ProgressBar';
import { Input } from '@/components/ui/Input';
import { Alert } from '@/components/ui/Alert';
import { cn } from '@/lib/utils';
import {
  Check,
  CheckCircle2,
  Cpu,
  Loader2,
  Send,
} from 'lucide-react';

export function OnboardingPage() {
  const { onboarding, refreshOnboarding, logout } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [currentStep, setCurrentStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Step 1: OAuth LLM
  const [llmConnected, setLlmConnected] = useState(false);
  const [checkingLlm, setCheckingLlm] = useState(false);
  const [oauthState, setOauthState] = useState('');
  const [oauthCode, setOauthCode] = useState('');
  const [waitingForCode, setWaitingForCode] = useState(false);
  const [showManualToken, setShowManualToken] = useState(false);
  const [manualToken, setManualToken] = useState('');

  // Step 2: Telegram integration
  const [botToken, setBotToken] = useState('');
  const [settingUpTelegram, setSettingUpTelegram] = useState(false);
  const [telegramConnected, setTelegramConnected] = useState(false);
  const [telegramBotUsername, setTelegramBotUsername] = useState('');
  const [skipping, setSkipping] = useState(false);

  // Detect OAuth callback
  useEffect(() => {
    const llmStatus = searchParams.get('llm');
    if (llmStatus === 'connected') {
      setLlmConnected(true);
      setCurrentStep(1);
      setSearchParams({}, { replace: true });
    } else if (llmStatus === 'error') {
      const reason = searchParams.get('reason') ?? 'desconhecido';
      setError(`Erro ao conectar Claude: ${reason}`);
      setCurrentStep(1);
      setSearchParams({}, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  // Check LLM status on mount
  const checkLlmStatus = useCallback(async () => {
    setCheckingLlm(true);
    try {
      const status = await api.llmStatus();
      if (status.connected) {
        setLlmConnected(true);
      }
    } catch { /* not connected */ }
    finally { setCheckingLlm(false); }
  }, []);

  useEffect(() => {
    checkLlmStatus();
  }, [checkLlmStatus]);

  // Sync with backend onboarding state
  useEffect(() => {
    if (onboarding) {
      if (onboarding.completed_steps.includes(1)) {
        setLlmConnected(true);
        setCurrentStep(2);
      }
    }
  }, [onboarding]);

  // --- Step 1 handlers ---

  async function handleOAuthConnect() {
    setError('');
    setCheckingLlm(true);
    try {
      const { redirectUrl, state } = await api.llmAuthorize();
      setOauthState(state);
      window.open(redirectUrl, '_blank');
      setWaitingForCode(true);
      setCheckingLlm(false);
    } catch {
      setCheckingLlm(false);
      setError('OAuth nao disponivel. Use o metodo manual.');
      setShowManualToken(true);
    }
  }

  async function handleCodeExchange() {
    const raw = oauthCode.trim();
    if (!raw) { setError('Cole o codigo'); return; }

    let code = raw;
    let state = oauthState;
    if (raw.includes('#')) {
      const parts = raw.split('#');
      code = parts[0];
      state = parts[1] || oauthState;
    }

    setLoading(true);
    setError('');
    try {
      await api.llmExchange(code, state);
      setLlmConnected(true);
      setWaitingForCode(false);
      // Complete step 1
      await api.completeOnboardingStep(1, { provider: 'anthropic' });
      await refreshOnboarding();
      setCurrentStep(2);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Codigo invalido. Tente novamente.');
    } finally {
      setLoading(false);
    }
  }

  async function handleManualToken() {
    if (!manualToken.trim()) { setError('Cole o token'); return; }
    setLoading(true);
    setError('');
    try {
      await api.completeOnboardingStep(1, {
        provider: 'anthropic',
        access_token: manualToken.trim(),
      });
      setLlmConnected(true);
      await refreshOnboarding();
      setCurrentStep(2);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Token invalido.');
    } finally {
      setLoading(false);
    }
  }

  async function handleAdvanceToStep2() {
    if (!llmConnected) return;
    setLoading(true);
    try {
      // Try completing step 1 if not already
      try {
        await api.completeOnboardingStep(1, { provider: 'anthropic' });
      } catch {
        // Already completed — ok
      }
      await refreshOnboarding();
      setCurrentStep(2);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro');
    } finally {
      setLoading(false);
    }
  }

  // --- Step 2 handlers ---

  async function handleTelegramSetup() {
    if (!botToken.trim()) { setError('Cole o token do bot do Telegram.'); return; }
    setSettingUpTelegram(true);
    setError('');
    try {
      const result = await api.telegramSetup(botToken.trim());
      setTelegramConnected(true);
      setTelegramBotUsername(result.bot_username ?? '');
      setBotToken('');
    } catch (err) {
      const msg = err instanceof Error ? err.message : '';
      if (msg.includes('webhook')) {
        setError('Token valido, mas o webhook falhou. Em localhost, use ngrok ou cloudflare tunnel pra expor a URL.');
      } else {
        setError(msg || 'Token invalido. Verifique com o BotFather.');
      }
    } finally {
      setSettingUpTelegram(false);
    }
  }

  async function handleCompleteOnboarding() {
    setLoading(true);
    setError('');
    try {
      await api.completeOnboardingStep(2, { telegram_connected: telegramConnected });
      await refreshOnboarding();
      window.location.href = '/';
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao finalizar onboarding.');
    } finally {
      setLoading(false);
    }
  }

  async function handleSkip() {
    setSkipping(true);
    setError('');
    try {
      await api.completeOnboardingStep(2, { telegram_connected: false, skipped: true });
      await refreshOnboarding();
      window.location.href = '/';
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao finalizar onboarding.');
    } finally {
      setSkipping(false);
    }
  }

  const progress = currentStep === 1
    ? (llmConnected ? 50 : 0)
    : (telegramConnected ? 100 : 55);

  // ─── Step 2: Telegram Integration ───────────────────────────────────────

  if (currentStep === 2) {
    return (
      <div className="min-h-screen bg-raios-bg flex flex-col items-center justify-center px-4">
        <div className="w-full max-w-lg">
          {/* Header */}
          <div className="text-center mb-8">
            <div className="inline-flex items-center justify-center w-12 h-12 rounded-xl bg-[#229ED9] mb-3">
              <Send className="text-white h-6 w-6" aria-hidden="true" />
            </div>
            <h1 className="text-2xl font-bold text-raios-text">Conectar Telegram</h1>
            <p className="text-raios-muted text-sm mt-1">Opcional: receba mensagens do RAIOS no Telegram</p>
          </div>

          <div className="mb-6">
            <ProgressBar value={progress} label="Passo 2 de 2 — Telegram" />
            <div className="flex justify-center gap-4 mt-3">
              <div className="w-8 h-8 rounded-full flex items-center justify-center text-xs font-medium bg-raios-accent text-white">
                <Check className="w-4 h-4" aria-hidden="true" />
              </div>
              <div className={cn(
                'w-8 h-8 rounded-full flex items-center justify-center text-xs font-medium transition-all',
                telegramConnected
                  ? 'bg-raios-accent text-white'
                  : 'bg-raios-accent/20 text-raios-accent-text ring-2 ring-raios-accent/40'
              )}>
                {telegramConnected ? <Check className="w-4 h-4" aria-hidden="true" /> : '2'}
              </div>
            </div>
          </div>

          <Card>
            <CardHeader>
              <CardTitle>Telegram</CardTitle>
              <CardDescription>Conecte um bot do Telegram para interagir com o RAIOS por mensagem.</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {telegramConnected ? (
                  <>
                    <Alert variant="success">
                      <div className="flex items-center gap-2">
                        <CheckCircle2 className="h-4 w-4" aria-hidden="true" />
                        <span>
                          Telegram conectado{telegramBotUsername ? ` — @${telegramBotUsername}` : ''}!
                        </span>
                      </div>
                    </Alert>
                    <Button variant="brand" className="w-full" onClick={handleCompleteOnboarding} loading={loading}>
                      Concluir configuracao
                    </Button>
                  </>
                ) : (
                  <>
                    <div className="bg-raios-surface rounded-lg p-3 space-y-2">
                      <p className="text-xs font-medium text-raios-text-secondary">Como conectar:</p>
                      <ol className="text-xs text-raios-muted space-y-1 list-decimal list-inside">
                        <li>Abra o <button type="button" onClick={() => window.open('https://t.me/BotFather', '_blank')} className="text-raios-accent-text hover:underline cursor-pointer">@BotFather</button> no Telegram</li>
                        <li>Envie <code className="font-mono text-raios-accent-text bg-raios-elevated px-1 py-0.5 rounded">/newbot</code> e siga as instrucoes</li>
                        <li>Copie o token do bot e cole abaixo</li>
                      </ol>
                    </div>
                    <Input
                      type="password"
                      value={botToken}
                      onChange={(e) => setBotToken(e.target.value)}
                      placeholder="123456:ABC-DEF1234ghIkl-zyx57W2v..."
                      label="Token do Bot"
                    />
                    <div className="flex gap-2">
                      <Button
                        variant="brand"
                        onClick={handleTelegramSetup}
                        loading={settingUpTelegram}
                        disabled={!botToken.trim()}
                        className="flex-1"
                      >
                        Conectar
                      </Button>
                      <Button variant="outline" onClick={handleSkip} loading={skipping}>
                        Pular
                      </Button>
                    </div>
                  </>
                )}
              </div>

              {error && (
                <Alert variant="destructive" className="mt-4" dismissible onDismiss={() => setError('')}>
                  {error}
                </Alert>
              )}

              <div className="mt-4 flex justify-start">
                <Button variant="ghost" size="sm" onClick={logout}>Sair</Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  // ─── Step 1: Connect LLM ───────────────────────────────────────────────

  return (
    <div className="min-h-screen bg-raios-bg flex flex-col items-center justify-center px-4">
      <div className="w-full max-w-lg">
        {/* Header */}
        <div className="text-center mb-8">
          <img
            src="/raios-mark.png"
            alt="RAIOS"
            className="inline-block w-12 h-12 rounded-xl mb-3 object-cover"
          />
          <h1 className="text-2xl font-bold text-raios-text">Bem-vindo ao RAIOS</h1>
          <p className="text-raios-muted text-sm mt-1">Primeiro, conecte sua conta Claude</p>
        </div>

        <div className="mb-6">
          <ProgressBar value={progress} label="Passo 1 de 2 — Conectar IA" />
          <div className="flex justify-center gap-4 mt-3">
            <div className={cn(
              'w-8 h-8 rounded-full flex items-center justify-center text-xs font-medium transition-all',
              llmConnected
                ? 'bg-raios-accent text-white'
                : 'bg-raios-accent/20 text-raios-accent-text ring-2 ring-raios-accent/40'
            )}>
              {llmConnected ? <Check className="w-4 h-4" aria-hidden="true" /> : '1'}
            </div>
            <div className="w-8 h-8 rounded-full flex items-center justify-center text-xs font-medium bg-raios-elevated text-raios-muted">
              2
            </div>
          </div>
        </div>

        <Card>
          <CardHeader>
            <CardTitle>Conectar IA</CardTitle>
            <CardDescription>Conecte sua conta Anthropic para ativar o Companion.</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {checkingLlm ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="h-6 w-6 text-raios-accent animate-spin" aria-hidden="true" />
                  <span className="ml-2 text-sm text-raios-muted">Verificando conexao...</span>
                </div>
              ) : llmConnected ? (
                <>
                  <Alert variant="success">
                    <div className="flex items-center gap-2">
                      <CheckCircle2 className="h-4 w-4" aria-hidden="true" />
                      <span>Claude conectado!</span>
                    </div>
                  </Alert>
                  <Button variant="brand" className="w-full" onClick={handleAdvanceToStep2} loading={loading}>
                    Continuar
                  </Button>
                </>
              ) : waitingForCode ? (
                <div className="space-y-4">
                  <Alert variant="default">
                    Uma aba foi aberta. Apos autorizar, copie o codigo que aparece na tela.
                  </Alert>
                  <Input
                    label="Codigo de autorizacao"
                    value={oauthCode}
                    onChange={(e) => setOauthCode(e.target.value)}
                    placeholder="Ex: AbCdEfGh...#1234abcd..."
                    helperText="Cole o codigo completo (incluindo # e tudo depois)."
                  />
                  <div className="flex gap-2">
                    <Button variant="brand" onClick={handleCodeExchange} loading={loading} disabled={!oauthCode.trim()} className="flex-1">
                      Confirmar
                    </Button>
                    <Button variant="outline" onClick={() => { setWaitingForCode(false); setOauthCode(''); }}>
                      Voltar
                    </Button>
                  </div>
                </div>
              ) : (
                <>
                  <div className="text-center py-4">
                    <div className="w-14 h-14 rounded-2xl bg-raios-accent/15 flex items-center justify-center mx-auto mb-3">
                      <Cpu className="h-7 w-7 text-raios-accent-text" aria-hidden="true" />
                    </div>
                    <p className="text-sm text-raios-text-secondary mb-4">
                      Conecte sua conta Anthropic para o Companion funcionar.
                    </p>
                    <Button variant="brand" size="lg" onClick={handleOAuthConnect}>
                      <Cpu className="h-4 w-4" aria-hidden="true" />
                      Conectar com Claude
                    </Button>
                    <p className="text-xs text-raios-muted mt-3">
                      Uma nova aba vai abrir. Depois, copie o codigo e cole aqui.
                    </p>
                  </div>

                  {showManualToken && (
                    <div className="border-t border-raios-border pt-4 space-y-3">
                      <p className="text-xs font-medium text-raios-text-secondary uppercase tracking-wider">Metodo alternativo</p>
                      <div className="bg-raios-surface rounded-lg p-3 space-y-1.5">
                        <ol className="text-xs text-raios-muted space-y-1 list-decimal list-inside">
                          <li>Execute: <code className="font-mono text-raios-accent-text bg-raios-elevated px-1 py-0.5 rounded">claude auth token</code></li>
                          <li>Cole o token abaixo</li>
                        </ol>
                      </div>
                      <Input type="password" value={manualToken} onChange={(e) => setManualToken(e.target.value)} placeholder="sk-ant-oat01-..." label="Token Claude" />
                      <Button variant="brand" onClick={handleManualToken} loading={loading} disabled={!manualToken.trim()} className="w-full">
                        Conectar com token
                      </Button>
                    </div>
                  )}

                  {!showManualToken && (
                    <button type="button" onClick={() => setShowManualToken(true)} className="block mx-auto text-xs text-raios-muted hover:text-raios-text-secondary transition-colors cursor-pointer">
                      Metodo manual
                    </button>
                  )}
                </>
              )}
            </div>

            {error && (
              <Alert variant="destructive" className="mt-4" dismissible onDismiss={() => setError('')}>
                {error}
              </Alert>
            )}

            <div className="mt-4 flex justify-start">
              <Button variant="ghost" size="sm" onClick={logout}>Sair</Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
